import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import type { NurseryLocation, NurseryTransfer } from '../../../types/nursery';

/**
 * Bring a load's stock to its despatch farm: one transfer from each farm the
 * allocated trees stand at, each line carrying its allocation so the order is
 * allocated at the despatch farm once the trees are. The trees need somewhere
 * to go there — a despatch area is offered first.
 */

export interface LoadTransfersDialogProps {
  loadId: number;
  despatchFarmId: number;
  despatchFarmName: string;
  defaultDate: string;
  onClose: () => void;
  onCreated: (transfers: NurseryTransfer[]) => void;
}

export function LoadTransfersDialog({ loadId, despatchFarmId, despatchFarmName, defaultDate, onClose, onCreated }: LoadTransfersDialogProps) {
  const [locations, setLocations] = useState<NurseryLocation[] | null>(null);
  const [locationId, setLocationId] = useState<number | ''>('');
  const [date, setDate] = useState(defaultDate);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    nurseryService.getLocations({ farm_id: despatchFarmId, active: true })
      .then((res) => {
        const list = res.success ? res.data || [] : [];
        list.sort((a, b) => a.name.localeCompare(b.name));
        setLocations(list);
        if (list[0]) setLocationId(list[0].id);
      })
      .catch((e) => { setLocations([]); setError(errorMessage(e, 'Could not load locations')); });
  }, [despatchFarmId]);

  const create = async () => {
    if (!locationId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.createTransfersForLoad(loadId, locationId, date);
      if (res.success) onCreated(res.data);
      else setError(errorMessage(res, 'Could not raise the transfers'));
    } catch (e) {
      setError(errorMessage(e, 'Could not raise the transfers'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" aria-labelledby="load-transfer-title">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="min-w-0">
            <h2 id="load-transfer-title" className="text-lg font-semibold text-neutral-900">Move the stock to {despatchFarmName}</h2>
            <p className="text-sm text-neutral-500">One transfer from each farm this load's trees are at.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-neutral-100 flex-shrink-0">
            <X className="h-5 w-5 text-neutral-500" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{error}</div>}
          {locations && locations.length === 0 ? (
            <p className="text-sm text-amber-800">
              {despatchFarmName} has no nursery locations yet, so there is nowhere to put the trees. Add one under Locations first.
            </p>
          ) : (
            <>
              <div>
                <label htmlFor="lt-location" className="block text-sm font-medium text-neutral-700 mb-1">Put them in</label>
                <select id="lt-location" value={locationId} onChange={(e) => setLocationId(e.target.value ? Number(e.target.value) : '')}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
                  {(locations ?? []).map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="lt-date" className="block text-sm font-medium text-neutral-700 mb-1">Move on</label>
                <input id="lt-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </>
          )}
        </div>
        <div className="flex flex-wrap justify-end gap-2 px-5 pb-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={create} disabled={busy || !locationId}>Raise transfers</Button>
        </div>
      </div>
    </div>
  );
}

export default LoadTransfersDialog;
